import GIF from "gif.js";
import { QrCode } from "../../../models/QrCode";
import { Me } from "../../../managers/Me";
import { getRandomAvatarUrl } from "../../global";

const worker = new URL("../../../public/gif.worker.js", import.meta.url).href;
const avatar = new URL("../../../public/avatar.webp", import.meta.url).href;

//TODO: later fix thin black border around edge of gif

export class Gif {
    private me = new Me();
    private qrCode = new QrCode();
    private gif: GIF | null = null;

    private canvas: HTMLCanvasElement;
    private ctx: CanvasRenderingContext2D;

    private heart: HTMLImageElement | null = null;
    private logicalSize = 300; // size of hb.svg
    private currentPadding = 10; // padding for heart

    private code: HTMLImageElement | null = null;
    private color: "#111" | "#003366" = "#111"; // black or white
    private rotation: 0 | 90 | 180 | 270 = 180;
    // 0 = link, vendor
    // 90 = trove
    // 180 = vault, vendor
    // 270 = stamp

    private avatar: HTMLImageElement | null = null;
    private avatarBorderColor = "gray";
    private avatarBorderWidth = 5; // logical pixels
    private avatarBackgroundColor = "transparent";

    private transparentColorHex = "#000"; // cannot use white; adding alpha channel breaks animation
    private isAnimating = false;
    private isCapturing = false;
    private isGifCreated = false;
    private animationFrameId: number | null = null;
    private startTime: number | null = null;
    private isAvatar = false;

    // properties for frame throttling
    private readonly ANIMATION_TARGET_FPS = 60; // 60fps = HD = 8 MB
    private frameSkipCounter = 0;
    private framesToSkip = 0;
    private gifFrameDelay = 0;

    constructor(canvas: HTMLCanvasElement) {
        this.canvas = canvas;
        // for high resolution
        const dpr = window.devicePixelRatio || 1;
        // Set the physical size of the canvas
        this.canvas.width = this.logicalSize * dpr;
        this.canvas.height = this.logicalSize * dpr;
        // Set the display size of the canvas (CSS pixels)
        this.canvas.style.width = `${this.logicalSize}px`;
        this.canvas.style.height = `${this.logicalSize}px`;
        this.ctx = this.canvas.getContext("2d", { willReadFrequently: true })!;
        // Scale the context to match the device pixel ratio
        this.ctx.imageSmoothingEnabled = false;
        this.ctx.scale(dpr, dpr);
    }

    async initHeart(karat: string) {
        const heart =
            "data:image/svg+xml;base64," +
            btoa(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="300" height="300">
                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" 
                fill="${karat}" transform="rotate(45 12 12) translate(0, 2)"/></svg>`);

        this.heart = await this.loadImage(heart);
    }

    // local storage
    async renderCode(
        data: string,
        isBlack: boolean,
        isAnimating: boolean,
        rotation: 0 | 90 | 180 | 270 = 180
    ): Promise<Blob> {
        this.isAvatar = false;
        await this.initHeart("silver");
        const code = await this.qrCode.mint(isBlack, data);
        this.code = await this.loadImage(code);
        this.color = isBlack ? "#111" : "#003366";
        this.rotation = rotation;

        if (isAnimating) {
            this.startAnimation();
            return this.captureAnimation(3, 25); // 1.3 MB
        } else {
            this.ctx.clearRect(0, 0, this.logicalSize, this.logicalSize);
            this.ctx.fillStyle = this.transparentColorHex;
            this.ctx.fillRect(0, 0, this.logicalSize, this.logicalSize);
            this.drawHeart(1.33); // Draw at normal scale (1.0)
            this.drawCode(false);
            return this.captureStatic();
        }
    }

    // cloud storage: TODO: later: see avatar component for other props to render
    async renderAvatar(uid: string, key: string, isAnimating: boolean) {
        this.isAvatar = true;
        await this.initHeart("silver");
        const profile = await this.me.getProfile(key);

        if (profile) {
            this.avatar = await this.loadImage(profile.photoURL || avatar); // public
            this.avatarBackgroundColor = "skyblue"; // TODO: aura
            this.avatarBorderColor = profile.phoneNumber ? "gold" : "silver"; // TODO: later add in gold and platinum;
        } else {
            const profile = await this.me.getUserProfile(uid);
            this.avatar = await this.loadImage(getRandomAvatarUrl()); // anonymous
            this.avatarBackgroundColor = "pink"; // TODO: aura
            this.avatarBorderColor = profile.phoneNumber ? "gold" : "silver"; // TODO: membership level
        }

        if (isAnimating) {
            this.startAnimation();
            return this.captureAnimation(3, 25);
        } else {
            this.ctx.clearRect(0, 0, this.logicalSize, this.logicalSize);
            this.ctx.fillStyle = this.transparentColorHex;
            this.ctx.fillRect(0, 0, this.logicalSize, this.logicalSize);
            this.drawHeart(1.33);
            this.drawAvatar(false);
            return this.captureStatic();
        }
    }

    ///

    private captureAnimation(
        durationInSeconds: number,
        targetFrameCount: number
    ): Promise<Blob> {
        this.gif = new GIF({
            workers: 2,
            quality: 1, // 1 to 20 (best to worst)
            workerScript: worker,
            width: this.canvas.width,
            height: this.canvas.height,
            transparent: this.transparentColorHex,
            dither: false
        });

        const framesToCapturePerSecond = targetFrameCount / durationInSeconds;
        this.framesToSkip = Math.max(
            1,
            Math.round(this.ANIMATION_TARGET_FPS / framesToCapturePerSecond)
        );
        this.gifFrameDelay = 1000 / framesToCapturePerSecond; // The delay between frames in the final GIF, in milliseconds. // e.g., for 5fps, delay is 1000ms / 5 = 200ms.
        this.frameSkipCounter = 0; // Reset the counter

        const capturePromise = new Promise<Blob>((resolve) => {
            this.gif!.on("finished", (blob) => {
                console.log("GIF rendering finished.");
                this.gif = null;
                this.isGifCreated = true;
                //this.stopAnimation();
                resolve(blob);
            });
        });

        this.isCapturing = true;
        console.log(
            `Starting GIF capture for ${durationInSeconds}s, aiming for ${targetFrameCount} frames.`
        );
        console.log(
            `Capturing 1 frame every ${this.framesToSkip} animation frames. GIF delay: ${this.gifFrameDelay}ms.`
        );

        setTimeout(() => {
            this.isCapturing = false;
            console.log("Stopping capture and starting render...");
            this.gif?.render();
        }, durationInSeconds * 1000);

        return capturePromise;
    }

    private captureStatic(): Promise<Blob> {
        this.gif = new GIF({
            workers: 2,
            quality: 1,
            workerScript: worker,
            width: this.canvas.width,
            height: this.canvas.height,
            transparent: this.transparentColorHex,
            dither: false
        });

        // Add just the single, currently drawn frame.
        this.gif.addFrame(this.canvas, { copy: true, delay: 0 });

        return new Promise((resolve) => {
            this.gif!.on("finished", (blob) => {
                this.gif = null;
                this.isGifCreated = true;
                this.ctx.clearRect(0, 0, this.logicalSize, this.logicalSize);
                this.drawHeart(1.33);
                this.isAvatar ? this.drawAvatar(false) : this.drawCode(false);
                resolve(blob);
            });
            this.gif!.render();
        });
    }

    private loadImage(url: string): Promise<HTMLImageElement> {
        return new Promise((resolve, reject) => {
            const image = new Image();
            image.crossOrigin = "anonymous";
            image.addEventListener("load", () => {
                resolve(image);
            });
            image.addEventListener("error", (err) => {
                reject(err);
            });
            image.src = url;
        });
    }

    /// draw

    private drawAvatar(isAnimating: boolean) {
        if (!this.avatar) return;

        // 1. --- Calculate Geometry ---
        const padding = isAnimating ? 160 : 120;
        const availableSize = this.logicalSize - padding;
        const outerRadius = availableSize / 2; // Radius of the entire element, including border

        const offsetX = isAnimating ? 30 : 35;
        const offsetY = isAnimating ? 30 : 35;
        const centerX = this.logicalSize / 2 - offsetX;
        const centerY = this.logicalSize / 2 + offsetY;

        // 2. --- Start Drawing ---
        this.ctx.save();

        // 3. --- Draw the OUTER BORDER circle ---
        this.ctx.beginPath();
        this.ctx.arc(centerX, centerY, outerRadius, 0, Math.PI * 2);
        this.ctx.fillStyle = this.avatarBorderColor;
        this.ctx.fill();

        // 4. --- Draw the INNER BACKGROUND circle ---
        const innerRadius = outerRadius - this.avatarBorderWidth;
        if (this.avatarBackgroundColor && innerRadius > 0) {
            this.ctx.beginPath();
            this.ctx.arc(centerX, centerY, innerRadius, 0, Math.PI * 2);
            this.ctx.fillStyle = this.avatarBackgroundColor;
            this.ctx.fill();
        }

        // 5. --- Create Circular Clipping Path for the Image ---
        this.ctx.beginPath();
        this.ctx.arc(centerX, centerY, innerRadius, 0, Math.PI * 2);
        this.ctx.clip();

        // 6. --- Draw the Avatar Image ---
        const image = this.avatar;
        const aspect = image.width / image.height;
        const imageDiameter = innerRadius * 2;

        let srcX = 0;
        let srcY = 0;
        let srcWidth = image.width;
        let srcHeight = image.height;

        if (aspect > 1) {
            // Landscape
            srcWidth = image.height;
            srcX = (image.width - srcWidth) / 2;
        } else {
            // Portrait or square
            srcHeight = image.width;
            srcY = (image.height - srcHeight) / 2;
        }

        if (innerRadius > 0) {
            this.ctx.drawImage(
                image,
                srcX,
                srcY,
                srcWidth,
                srcHeight,
                centerX - innerRadius,
                centerY - innerRadius,
                imageDiameter,
                imageDiameter
            );
        }

        // 7. --- Restore Canvas State (remove clipping) ---
        this.ctx.restore();

        // 8. --- Draw "SM" text at bottom-right of avatar circle ---
        this.ctx.font = isAnimating ? "8px sans-serif" : "10px sans-serif";
        this.ctx.fillStyle = "white";
        this.ctx.textAlign = "right";
        this.ctx.textBaseline = "bottom";

        this.ctx.fillText(
            "SM",
            centerX + innerRadius + (isAnimating ? 10 : 10),
            centerY + innerRadius - (isAnimating ? 10 : 15)
        );
    }

    // no SM
    private drawAvatar2(isAnimating: boolean) {
        if (!this.avatar) return;

        // 1. --- Calculate Geometry ---
        const padding = isAnimating ? 160 : 120;
        const availableSize = this.logicalSize - padding;
        const outerRadius = availableSize / 2; // Radius of the entire element, including border

        const offsetX = isAnimating ? 30 : 35;
        const offsetY = isAnimating ? 30 : 35;
        const centerX = this.logicalSize / 2 - offsetX;
        const centerY = this.logicalSize / 2 + offsetY;

        // 2. --- Start Drawing ---
        this.ctx.save();

        // 3. --- Draw the OUTER BORDER circle ---
        // This is the back-most layer.
        this.ctx.beginPath();
        this.ctx.arc(centerX, centerY, outerRadius, 0, Math.PI * 2);
        this.ctx.fillStyle = this.avatarBorderColor;
        this.ctx.fill();

        // 4. --- Draw the INNER BACKGROUND circle ---
        // This is drawn on top of the border, creating the ring effect.
        // This is also the area where the image will be placed.
        const innerRadius = outerRadius - this.avatarBorderWidth;
        if (this.avatarBackgroundColor && innerRadius > 0) {
            this.ctx.beginPath();
            this.ctx.arc(centerX, centerY, innerRadius, 0, Math.PI * 2);
            this.ctx.fillStyle = this.avatarBackgroundColor;
            this.ctx.fill();
        }

        // 5. --- Create Circular Clipping Path for the Image ---
        // The path is the same size as the inner background circle.
        this.ctx.beginPath();
        this.ctx.arc(centerX, centerY, innerRadius, 0, Math.PI * 2);
        this.ctx.clip(); // Restrict subsequent drawing to this path

        // 6. --- Draw the Avatar Image ---
        // This logic crops the source image to a square and then draws it into
        // the circular clipping area, preventing distortion.
        const image = this.avatar;
        const aspect = image.width / image.height;
        const imageDiameter = innerRadius * 2;

        let srcX = 0;
        let srcY = 0;
        let srcWidth = image.width;
        let srcHeight = image.height;

        if (aspect > 1) {
            // Landscape
            srcWidth = image.height;
            srcX = (image.width - srcWidth) / 2;
        } else {
            // Portrait or square
            srcHeight = image.width;
            srcY = (image.height - srcHeight) / 2;
        }

        if (innerRadius > 0) {
            this.ctx.drawImage(
                image,
                srcX,
                srcY,
                srcWidth,
                srcHeight,
                centerX - innerRadius, // Destination X
                centerY - innerRadius, // Destination Y
                imageDiameter, // Destination Width
                imageDiameter // Destination Height
            );
        }

        // 7. --- Restore Canvas State ---
        // This is crucial to remove the clipping path.
        this.ctx.restore();
    }

    // qr code + rotation + sm
    private drawCode(isAnimating: boolean) {
        if (!this.code) return;

        const padding = isAnimating ? 170 : 130;
        const availableWidth = this.logicalSize - padding;
        const availableHeight = this.logicalSize - padding;

        const scale = Math.min(
            availableWidth / this.code.width,
            availableHeight / this.code.height
        );

        const destWidth = this.code.width * scale;
        const destHeight = this.code.height * scale;

        const offsetX = isAnimating ? 38 : 45;
        const offsetY = isAnimating ? 38 : 45;

        const destX = (this.logicalSize - destWidth) / 2 - offsetX;
        const destY = (this.logicalSize - destHeight) / 2 + offsetY;

        // Save context state
        this.ctx.save();

        // Translate to center of image, rotate (degrees → radians), then draw
        const centerX = destX + destWidth / 2;
        const centerY = destY + destHeight / 2;

        this.ctx.translate(centerX, centerY);
        this.ctx.rotate((this.rotation * Math.PI) / 180);

        // Draw image centered at rotation pivot
        this.ctx.drawImage(
            this.code,
            -destWidth / 2,
            -destHeight / 2,
            destWidth,
            destHeight
        );

        // Restore context
        this.ctx.restore();

        // --- Draw "SM" (not rotated) ---
        this.ctx.save();
        this.ctx.font = isAnimating ? "8px sans-serif" : "10px sans-serif";
        this.ctx.fillStyle = this.color;
        this.ctx.textAlign = "right";
        this.ctx.textBaseline = "bottom";

        // Position "SM" relative to the unrotated image placement
        const textX = destX + destWidth + (isAnimating ? 20 : 25);
        const textY = destY + destHeight - (isAnimating ? 10 : 10);

        this.ctx.fillText("SM", textX, textY);
        this.ctx.restore();
    }

    // qr code + rotation
    private drawCode2(isAnimating: boolean) {
        if (!this.code) return;

        const padding = isAnimating ? 170 : 130;
        const availableWidth = this.logicalSize - padding;
        const availableHeight = this.logicalSize - padding;

        const scale = Math.min(
            availableWidth / this.code.width,
            availableHeight / this.code.height
        );

        const destWidth = this.code.width * scale;
        const destHeight = this.code.height * scale;

        const offsetX = isAnimating ? 38 : 45;
        const offsetY = isAnimating ? 38 : 45;

        const destX = (this.logicalSize - destWidth) / 2 - offsetX;
        const destY = (this.logicalSize - destHeight) / 2 + offsetY;

        // Save context state
        this.ctx.save();

        // Translate to center of image, rotate (convert degrees → radians), then draw
        const centerX = destX + destWidth / 2;
        const centerY = destY + destHeight / 2;

        this.ctx.translate(centerX, centerY);
        this.ctx.rotate((this.rotation * Math.PI) / 180);
        this.ctx.drawImage(
            this.code,
            -destWidth / 2,
            -destHeight / 2,
            destWidth,
            destHeight
        );

        // Restore context
        this.ctx.restore();
    }

    // qr code
    private drawCode3(isAnimating: boolean) {
        if (!this.code) return;
        //this.ctx.clearRect(0, 0, this.logicalSize, this.logicalSize);

        const padding = isAnimating ? 170 : 130;
        const availableWidth = this.logicalSize - padding;
        const availableHeight = this.logicalSize - padding;

        const scale = Math.min(
            availableWidth / this.code.width,
            availableHeight / this.code.height
        );

        const destWidth = this.code.width * scale;
        const destHeight = this.code.height * scale;

        const offsetX = isAnimating ? 38 : 45;
        const offsetY = isAnimating ? 38 : 45;

        const destX = (this.logicalSize - destWidth) / 2 - offsetX;
        const destY = (this.logicalSize - destHeight) / 2 + offsetY;

        this.ctx.drawImage(this.code, destX, destY, destWidth, destHeight);
    }

    /// heartbeat animation

    public startAnimation() {
        if (this.isAnimating) return; // Don't start multiple loops

        console.log("Starting animation...");
        this.isAnimating = true;
        this.startTime = null; // Reset start time for the new animation

        // Bind 'this' to the animation loop function
        const loop = this.animationLoop.bind(this);
        this.animationFrameId = requestAnimationFrame(loop);
    }

    public stopAnimation() {
        if (!this.isAnimating) return;

        console.log("Stopping animation.");
        this.isAnimating = false;
        if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = null;
        }
    }

    private animationLoop(timestamp: number) {
        if (!this.isAnimating) return;

        if (!this.startTime) {
            this.startTime = timestamp;
        }

        // Calculate how far into the animation we are
        const elapsed = timestamp - this.startTime;
        const duration = 1500; // Animation duration in ms (1.5 seconds)
        const progress = (elapsed % duration) / duration; // Value from 0.0 to 1.0

        // Get the current scale based on our keyframe logic
        const scale = this.getScaleForProgress(progress);

        // Draw the frame with the calculated scale
        if (this.isGifCreated) {
            this.ctx.clearRect(0, 0, this.logicalSize, this.logicalSize);
        } else {
            this.ctx.fillStyle = this.transparentColorHex;
            this.ctx.fillRect(0, 0, this.logicalSize, this.logicalSize);
        }

        this.drawHeart(scale);
        this.isAvatar ? this.drawAvatar(true) : this.drawCode(true);

        if (this.isCapturing) {
            this.frameSkipCounter++;
            if (this.frameSkipCounter >= this.framesToSkip) {
                this.gif?.addFrame(this.canvas, {
                    copy: true,
                    delay: this.gifFrameDelay
                });
                this.frameSkipCounter = 0; // Reset counter for the next skip cycle
            }
        }

        // Request the next frame
        this.animationFrameId = requestAnimationFrame(
            this.animationLoop.bind(this)
        );
    }

    private drawHeart(animationScale: number) {
        if (!this.heart) return;
        //this.ctx.clearRect(0, 0, this.logicalSize, this.logicalSize);

        // 1. Calculate the base size of the heart with padding (when scale is 1.0)
        const availableWidth = this.logicalSize - this.currentPadding;
        const availableHeight = this.logicalSize - this.currentPadding;

        const layoutScale = Math.min(
            availableWidth / this.heart.width,
            availableHeight / this.heart.height
        );
        const baseWidth = this.heart.width * layoutScale;
        const baseHeight = this.heart.height * layoutScale;

        // 2. Apply the animation scale to this base size
        const destWidth = baseWidth * animationScale;
        const destHeight = baseHeight * animationScale;

        // 3. Center the final scaled image on the canvas
        const centerX = this.logicalSize / 2;
        const centerY = this.logicalSize / 2;
        const destX = centerX - destWidth / 2 - 12;
        const destY = centerY - destHeight / 2 + 13;

        this.ctx.drawImage(this.heart, destX, destY, destWidth, destHeight);
    }

    /**
     * Translates CSS keyframe percentages into a scale value.
     */
    private getScaleForProgress(progress: number): number {
        // Keyframe percentages converted to progress (0.0 to 1.0)
        const p1 = 0.14; // 14%
        const p2 = 0.28; // 28%
        const p3 = 0.42; // 42%
        const p4 = 0.7; // 70%

        const maxScale = 1.3;

        if (progress < p1) {
            // 0% -> 14% (scale up)
            return this.lerp(1, maxScale, progress / p1);
        } else if (progress < p2) {
            // 14% -> 28% (scale down)
            return this.lerp(maxScale, 1, (progress - p1) / (p2 - p1));
        } else if (progress < p3) {
            // 28% -> 42% (scale up)
            return this.lerp(1, maxScale, (progress - p2) / (p3 - p2));
        } else if (progress < p4) {
            // 42% -> 70% (scale down)
            return this.lerp(maxScale, 1, (progress - p3) / (p4 - p3));
        } else {
            // 70% -> 100% (stay at 1)
            return 1;
        }
    }

    /**
     * Linear interpolation helper function (lerp).
     * Calculates a value between 'a' and 'b' based on 't' (0.0 to 1.0).
     */
    private lerp(a: number, b: number, t: number): number {
        return a * (1 - t) + b * t;
    }
}
